import React from 'react';
import {
  FileExtensions, IUploadConfig, UploadPaths, UploadResponse, UploadScenarios,
} from 'interfaces/file-uploader';
import { IRestApiResponse } from 'interfaces';
import axios from 'axios';
import ENVIRONMENT from 'config';
import { getAuthToken } from 'helpers/token';
import { isArray } from 'lodash';
import { useSelector } from 'react-redux';
import { selectEnterprise } from '../selectors';

export enum FileType {
  IMAGE = 'image',
  VIDEO = 'video',
  AUDIO = 'audio',
  DOCUMENT = 'document',
  FOLDER = 'folder',
  FONT = 'font',
}

const mbSize = 1000000;

const videos = [
  FileExtensions.mp4,
  FileExtensions.flv,
  FileExtensions.mkv,
  FileExtensions.webm,
  FileExtensions.mov,
  FileExtensions.wmv,
];
const images = [FileExtensions.jpg, FileExtensions.jpeg, FileExtensions.png, FileExtensions.gif];
const documents = [
  FileExtensions.pdf,
  FileExtensions.ppt,
  FileExtensions.pptx,
  FileExtensions.xls,
  FileExtensions.xlsx,
  FileExtensions.csv,
];
const fonts = [FileExtensions.woff, FileExtensions.woff2];

export const uploadConfig = new Map<number, IUploadConfig>([
  [
    UploadScenarios.expertVideo,
    { allowedExtensions: [...videos], sizeLimit: 500 * mbSize },
  ],
  [
    UploadScenarios.expertProfile,
    { allowedExtensions: [...images], sizeLimit: 30 * mbSize },
  ],
  [
    UploadScenarios.expertSponsor,
    { allowedExtensions: [...images], sizeLimit: 500 * mbSize },
  ],
  [
    UploadScenarios.expertBadge,
    { allowedExtensions: [...images], sizeLimit: 30 * mbSize },
  ],
  [
    UploadScenarios.expertContent,
    {
      allowedExtensions: [...images, ...videos, ...documents],
      sizeLimit: 5120 * mbSize,
      sizeLimitsByType: {
        [FileType.DOCUMENT]: 30 * mbSize,
      },
    },
  ],
  [
    UploadScenarios.sessionHighlightMedia,
    { allowedExtensions: [...images, ...videos], sizeLimit: 500 * mbSize },
  ],
  [
    UploadScenarios.articleBanner,
    { allowedExtensions: [...images], sizeLimit: 100 * mbSize },
  ],
  [
    UploadScenarios.sessionBanner,
    { allowedExtensions: [...images], sizeLimit: 30 * mbSize },
  ],
  [
    UploadScenarios.sessionFeedback,
    { allowedExtensions: [...videos], sizeLimit: 9000 * mbSize }, // allow 9 GB
  ],
  [
    UploadScenarios.groupBanner,
    { allowedExtensions: [...images], sizeLimit: 100 * mbSize },
  ],
  [
    UploadScenarios.formBadge,
    { allowedExtensions: [...images], sizeLimit: 30 * mbSize },
  ],
  [
    UploadScenarios.generateThumbnail,
    { allowedExtensions: [FileExtensions.jpeg], sizeLimit: 2 * mbSize },
  ],
  [
    UploadScenarios.scorm,
    { allowedExtensions: [FileExtensions.zip], sizeLimit: 500 * mbSize },
  ],
  [
    UploadScenarios.textBlock,
    { allowedExtensions: [...images], sizeLimit: 500 * mbSize },
  ],
]);

export const extensionToType = new Map<string, string>([]);
videos.forEach((video) => {
  extensionToType.set(video, FileType.VIDEO);
});
images.forEach((image) => {
  extensionToType.set(image, FileType.IMAGE);
});
documents.forEach((document) => {
  extensionToType.set(document, FileType.DOCUMENT);
});
fonts.forEach((font) => {
  extensionToType.set(font, FileType.FONT);
});

const useFileUpload = () => {
  const [progress, setProgress] = React.useState(-1);
  const enterprise = useSelector(selectEnterprise);

  const onUploadProgress = (event: any) => {
    setProgress(Math.round((100 * event.loaded) / event.total));
  };

  const handleContentDataUpload = async (
    e: any,
    url: string,
    scenario: UploadScenarios,
    file?: File,
  ): Promise<UploadResponse> => {
    try {
      setProgress(0);
      const fileUploaded = file || e.target?.files?.[0] || e?.dataTransfer?.files?.[0];

      const config = uploadConfig.get(scenario);
      if (!config) {
        setProgress(-1);
        return {
          name: '',
          url,
          extension: '',
          category: '',
          statusCode: 400,
          message: 'Invalid configuration',
        };
      }

      if (fileUploaded) {
        const extension = fileUploaded.name.split('.').pop().toLowerCase();
        if (!config.allowedExtensions.includes(`.${extension}`)) {
          setProgress(-1);
          return {
            name: '',
            url,
            extension: '',
            category: '',
            statusCode: 400,
            message: `File type not allowed. Only ${config.allowedExtensions.join(', ')} file types are allowed`,
          };
        }
        const fileType = extensionToType.get(`.${extension}`) as string;
        let applicableSizeLimit = config.sizeLimit;

        if (config.sizeLimitsByType && fileType && config.sizeLimitsByType[fileType]) {
          applicableSizeLimit = config.sizeLimitsByType[fileType];
        }

        if (applicableSizeLimit < fileUploaded.size) {
          setProgress(-1);
          return {
            name: '',
            url,
            extension: '',
            category: '',
            statusCode: 400,
            message: `File is larger than the recommended size ${applicableSizeLimit / mbSize} MB`,
          };
        }

        const fileData: {
          fileName: string;
          fileSize: number;
          path?: UploadPaths;
        } = {
          fileName: fileUploaded.name,
          fileSize: fileUploaded.size,
        };

        if (scenario === UploadScenarios.sessionFeedback) {
          fileData.path = UploadPaths.REVIEW_TOOL;
        }

        try {
          const res: IRestApiResponse = await axios.post(`${ENVIRONMENT.BACKEND_API}/pre-signed-url/create-url`, fileData, {
            headers: {
              'Access-Control-Allow-Origin': '*',
              'Access-Control-Allow-Methods': 'GET,PUT,POST,DELETE,PATCH,OPTIONS',
              'Content-Type': 'application/json',
              redirect: 'follow',
              referrer: 'no-referrer',
              Authorization: `Bearer ${getAuthToken() || ''}`,
              'x-ent-id': enterprise.id,
            },
          });
          if (res.status === 201 && res.data && res.data.data) {
            const {
              uniqueFileName, presignedPost, contentType, acl,
              publicUrl, fileExtension, fileCategory,
            } = res.data.data;
            const formData = {
              ...presignedPost.fields,
              'Content-Type': contentType,
              acl,
              file: fileUploaded,
            };

            const res2: any = await axios.post(presignedPost.url, formData, {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
              onUploadProgress,
            });
            if (res2.status === 204) {
              setProgress(-1);
              return {
                name: uniqueFileName,
                url: publicUrl,
                extension: fileExtension,
                category: fileCategory,
                fileData,
                statusCode: res.data.statusCode,
                message: isArray(res.data.message) ? res.data.message[0] : 'success',
              };
            }
            setProgress(-1);
            return {
              name: '',
              url,
              extension: '',
              category: '',
              fileData,
              statusCode: 400,
              message: 'Upload failed',
            };
          }
        } catch (error) {
          setProgress(-1);
          return {
            name: '',
            url,
            extension: '',
            category: '',
            fileData,
            statusCode: 400,
            message: (error as Error).message,
          };
        }
      }
      setProgress(-1);
      return {
        name: '',
        url,
        extension: '',
        category: '',
        statusCode: 400,
        message: '',
      };
    } catch (error) {
      setProgress(-1);
      return {
        name: '',
        url,
        extension: '',
        category: '',
        statusCode: 400,
        message: (error as Error).message,
      };
    }
  };

  return {
    handleContentDataUpload,
    progress,
  };
};

export default useFileUpload;
